import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import authService from '../api/authService';
import { removeApiKey, removeTeamEmail, removeToken } from '../Utility/Utility';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const searchParams = new URLSearchParams(location.search);
  const visibleSidebarParams = searchParams.get('visiblesidbar')?.toLowerCase().split(',') || [];
  const preservedSearch = visibleSidebarParams.length > 0 ? `?${searchParams.toString()}` : '';

  const menuItems = [

    { id: 'customers', path: '/customers', label: 'Customers', icon: 'bi-people' },
    { id: 'dashboard', path: '/', label: 'Dashboard', icon: 'bi-speedometer2' },
    { id: 'products', path: '/products', label: 'Products', icon: 'bi-box-seam' },
    { id: 'quotes', path: '/quotations', label: 'Quotes', icon: 'bi-file-earmark-text' },
    { id: 'subscriptions', path: '/subscriptions', label: 'Sales', icon: 'bi-calendar-check' },
    { id: 'feedback', path: '/billing/feedback', label: 'Feedback & Follow-ups', icon: 'bi-chat-square-quote' },
    { id: 'logs', path: '/system-logs', label: 'System Logs', icon: 'bi-journal-text' },
    { id: 'settings', path: '/settings', label: 'Settings', icon: 'bi-gear' },
  ];

  const pageIdMap = {
    customers: 'customers',
    dashboard: 'dashboard',
    products: 'products',
    quotes: 'quotes',
    subscriptions: 'subscriptions',
    feedback: 'feedback',
    logs: 'logs',
    settings: 'settings'
  };

  useEffect(() => {
    let mounted = true;

    const loadProfile = async () => {
      try {
        const res = await authService.me();
        if (mounted && res.success) {
          setProfile(res.userInfo || null);
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
      }
    };

    loadProfile();
    return () => {
      mounted = false;
    };
  }, []);

  const initials = useMemo(() => {
    const source = profile?.name || profile?.email || 'U';
    return source
      .split(' ')
      .map((part) => part?.[0] || '')
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }, [profile]);

  const handleLogout = () => {
    removeToken();
    removeApiKey();
    removeTeamEmail();
    navigate('/login', { replace: true });
  };

  const allowedPages = profile?.permissions?.allowedPages || [];

  const filteredItems = (visibleSidebarParams.length > 0
    ? menuItems.filter(item => visibleSidebarParams.includes(item.id))
    : menuItems)
    .filter((item) => {
      if (!allowedPages.length || profile?.role === 'owner') {
        return true;
      }

      const permissionKey = pageIdMap[item.id];
      return permissionKey ? allowedPages.includes(permissionKey) : true;
    });

  return (
    <div className="horizontal-nav">
      <div className="nav-container container-fluid d-flex align-items-center">
        <div className="nav-items-wrapper d-flex align-items-center flex-grow-1">
          {filteredItems.map((item, index) => (
            <NavLink 
              key={index} 
              to={`${item.path}${preservedSearch}`}
              className={({ isActive }) => `nav-item-link ${isActive ? 'active' : ''}`}
            >
              <i className={`bi ${item.icon} me-2`}></i>
              <span className="link-text">{item.label}</span>
            </NavLink>
          ))}
        </div>

        <div className="d-flex align-items-center gap-2 ms-3">
          { /* API Docs 
          <Link to={`/api-docs${preservedSearch}`} className="btn btn-sm btn-outline-light">
            <i className="bi bi-journal-code me-1"></i>
            API Docs
          </Link>
          */

          }


          <div className="dropdown">
            <button
              className="btn btn-sm btn-outline-light dropdown-toggle d-flex align-items-center gap-2"
              type="button"
              data-bs-toggle="dropdown"
            >
              <span
                className="rounded-circle bg-light text-dark d-inline-flex align-items-center justify-content-center"
                style={{ width: '28px', height: '28px', fontSize: '0.75rem', fontWeight: 700 }}
              >
                {initials}
              </span>
              <span className="d-none d-md-inline">{profile?.name || 'Profile'}</span>
            </button>

            <div className="dropdown-menu dropdown-menu-end">
              <div className="px-3 py-2 border-bottom">
                <div className="fw-700">{profile?.name || 'Workspace User'}</div>
                <div className="small text-muted">{profile?.email || ''}</div>
              </div>
              <div className="px-3 py-2 small text-muted">
                <div><strong>Role:</strong> {profile?.role || 'owner'}</div>
                <div><strong>Workspace:</strong> {profile?.companyName || profile?.admin || 'Subscription SaaS'}</div>
              </div>
              {(profile?.role === 'owner' || allowedPages.includes('settings')) && (
                <Link to={`/settings${preservedSearch}`} className="dropdown-item">
                  <i className="bi bi-gear me-2"></i>
                  Settings
                </Link>
              )}
              {profile?.role === 'owner' && (
                <>
                {
                  /*
                  <Link to={`/api-keys${preservedSearch}`} className="dropdown-item">
                    <i className="bi bi-key me-2"></i>
                    API Keys
                  </Link>
                  */
                }


                  <Link to={`/team-members${preservedSearch}`} className="dropdown-item">
                    <i className="bi bi-people me-2"></i>
                    Team Members
                  </Link>
                  <Link to={`/roles${preservedSearch}`} className="dropdown-item">
                    <i className="bi bi-shield-check me-2"></i>
                    Roles
                  </Link>
                </>
              )}
              <button className="dropdown-item text-danger" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right me-2"></i>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
